import { Injectable, signal, inject } from '@angular/core';
import { Api } from '../api/api';
import { dashboardBlockchainsControllerListBlockchainsV1 } from '../api/functions';
import { BlockchainResponseDashboardDto } from '../api/models/blockchain-response-dashboard-dto';

export interface SelectOption<T = any> {
  label: string;
  value: T;
}

/**
 * Blockchains Lookup Service for Dashboard
 * Fetches blockchain list from dashboard backend API
 * Used for project creation/editing when selecting blockchain
 */
@Injectable({ providedIn: 'root' })
export class BlockchainsLookupService {
  private readonly api = inject(Api);
  private readonly _blockchains = signal<BlockchainResponseDashboardDto[]>([]);
  private readonly _options = signal<SelectOption<string>[]>([]);
  private readonly _labelById = new Map<string, string>();
  private _loaded = false;

  /**
   * Load blockchains from backend (cached). Optionally pass a search string for server-side filtering.
   */
  async load(search?: string, force = false): Promise<void> {
    if (this._loaded && !force && !search) return; // simple cache; bypass when searching

    const list = await this.api.invoke(dashboardBlockchainsControllerListBlockchainsV1, {
      search: search || undefined,
    });

    if (!search) {
      // Only cache default (no search) result
      this._loaded = true;
      this._blockchains.set(list);
      this._options.set(list.map((b) => ({ label: b.label, value: b.id })));
      this._labelById.clear();
      for (const b of list) this._labelById.set(b.id, b.label);
    }
  }

  /** Returns cached blockchains array */
  get blockchains() {
    return this._blockchains;
  }

  /** Options for PrimeNG Select: label + value=id */
  get options() {
    return this._options;
  }

  /** Lookup label by blockchain id; returns id if not found. */
  labelById(id?: string | null): string {
    if (!id) return '';
    return this._labelById.get(id) ?? id;
  }
}
